import React from 'react';
import { Award, Target, Users, Heart, TrendingUp, Shield } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import ContactForm from '../components/ContactForm';
import SEO from '../components/SEO';

const About = () => {
  const values = [
    {
      icon: Shield,
      title: 'Integrity',
      description: 'We operate with the highest ethical standards and always put our clients\' interests first.',
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'Our team of licensed professionals delivers exceptional service and superior results.',
    },
    {
      icon: Heart,
      title: 'Client-Focused',
      description: 'Every strategy we develop is tailored to your unique goals, values, and circumstances.',
    },
    {
      icon: Users,
      title: 'Holistic Approach',
      description: 'We provide integrated solutions across asset management, tax, and estate planning.',
    },
  ];

  const teamMembers = [
    {
      name: 'Financial Advisors',
      role: 'Licensed Investment Professionals',
      description: 'Our advisors bring decades of combined experience in wealth management and portfolio strategy.',
      image: 'https://images.unsplash.com/photo-1560520653-9e0e4c89eb11',
    },
    {
      name: 'In-House CPAs',
      role: 'Certified Public Accountants',
      description: 'Expert tax professionals dedicated to maximizing your wealth through strategic tax planning.',
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85',
    },
    {
      name: 'Estate Planning Attorneys',
      role: 'Licensed in All 50 States',
      description: 'Experienced legal professionals ensuring your legacy is protected nationwide.',
      image: 'https://images.unsplash.com/photo-1562564055-71e051d33c19',
    },
  ];

  // FAQ Schema for AI search engines
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What services does Paracca Financial Group offer?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Paracca Financial Group is a comprehensive financial advisory firm offering three core services nationwide: Wealth Management with direct indexing and options overlay strategies, Tax services with in-house CPAs, and Estate Planning with licensed attorneys available in all 50 states. We serve individuals and families throughout the United States."
        }
      },
      {
        "@type": "Question",
        "name": "Is Paracca Financial Group a fiduciary?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, Paracca Financial Group is a FINRA registered fiduciary investment advisory firm. As a fiduciary, we are legally obligated to act in the best interests of our clients at all times, putting your financial goals ahead of our own interests."
        }
      },
      {
        "@type": "Question",
        "name": "Where is Paracca Financial Group located?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our main office is located at 428 S Tamiami Trail, Osprey, FL 34229. While our office is in Florida, we serve clients nationwide in all 50 states. You can reach us at 941-545-9057 or contact@paracca.com."
        }
      },
      {
        "@type": "Question",
        "name": "What makes Paracca Financial Group different from other financial advisors?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We are the only firm offering comprehensive asset management, tax, and estate services under one roof. Our team includes licensed financial advisors, in-house CPAs, and estate attorneys, providing truly holistic financial planning. With over 50 years combined experience and licensing in all 50 states, we deliver integrated solutions that traditional advisors cannot match."
        }
      },
      {
        "@type": "Question",
        "name": "Do you provide legal advice?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Paracca Financial Group is not a law firm and does not provide legal advice. However, our in-house estate planning companies have licensed attorneys who provide legal services for estate planning, trusts, and wills. These legal services are separate from our investment advisory services."
        }
      },
      {
        "@type": "Question",
        "name": "What are your office hours?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our office hours are Monday through Friday, 8:30 AM to 4:00 PM EST. We're committed to providing personalized service to our clients nationwide."
        }
      }
    ]
  };

  const differentiators = [
    {
      icon: Target,
      title: 'Truly Holistic',
      description: 'The only firm in the region offering comprehensive asset management, tax planning, and estate planning services under one roof.',
    },
    {
      icon: Users,
      title: 'In-House Expertise',
      description: 'Unlike other firms that outsource, all our professionals—advisors, CPAs, and attorneys—work together in-house.',
    },
    {
      icon: TrendingUp,
      title: 'High Net Worth Focus',
      description: 'Specialized strategies designed specifically for individuals and families.',
    },
    {
      icon: Shield,
      title: 'Nationwide Service',
      description: 'Complete asset management, tax, and estate services available to clients in all 50 states.',
    },
  ];

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "AboutPage",
        "mainEntity": {
          "@type": "FinancialService",
          "name": "Paracca Financial Group",
          "description": "Premier holistic financial advisory firm serving individuals and families nationwide in all 50 states."
        }
      },
      faqSchema
    ]
  };

  return (
    <>
      <SEO 
        title="About Us | Paracca Financial Group - Nationwide Service"
        description="Learn about Paracca Financial Group, a premier holistic financial advisory firm serving clients nationwide. Licensed advisors, in-house CPAs, and attorneys in all 50 states."
        keywords="about paracca financial, financial advisors nationwide, wealth management firm, holistic financial services"
        schema={schema}
      />

      {/* Hero Section */}
      <section className="relative h-[400px] md:h-[500px] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('/hero-image.jpg')`,
          }}
        >
          <div className="absolute inset-0 bg-[#0a69b2] opacity-70"></div>
        </div>
        
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="max-w-3xl">
            <h1 className="font-bellefair text-4xl md:text-6xl  text-white mb-6 uppercase" style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.5)' }}>
              About Us
            </h1>
            <p className="font-montserrat text-lg md:text-xl text-white leading-relaxed" style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.5)' }}>
              A premier holistic financial advisory firm serving individuals and families nationwide
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-bellefair text-3xl md:text-4xl  text-[#0a69b2] mb-6 text-center uppercase">
              Our Story
            </h2>
            <div className="space-y-6 font-montserrat text-lg text-gray-600 leading-relaxed">
              <p>
                Paracca Financial Group was founded on a simple yet powerful vision: to provide truly holistic financial services to individuals and families nationwide.
              </p>
              <p>
                We recognized that most financial firms force clients to work with multiple disconnected providers—one for investments, another for taxes, and yet another for estate planning. This fragmented approach leads to inefficiencies, missed opportunities, and strategies that don't work together.
              </p>
              <p>
                Our solution was revolutionary: bring all three disciplines—asset management, tax planning, and estate planning—under one roof with fully integrated, in-house professionals. Today, we're proud to be the only firm in the region offering this comprehensive approach.
              </p>
              <p>
                With licensed financial advisors, in-house CPAs, and estate planning attorneys licensed in all 50 states, we deliver coordinated strategies that maximize wealth preservation and growth while minimizing taxes and protecting your legacy—serving clients nationwide.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-bellefair text-3xl md:text-4xl  text-[#0a69b2] mb-4 uppercase">
              Meet Our Team
            </h2>
            <p className="font-montserrat text-lg text-gray-600 max-w-3xl mx-auto">
              A dedicated team of professionals committed to your financial success
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Don Paracca */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition-shadow duration-300">
              <div className="relative h-80 overflow-hidden">
                <img 
                  src="/don-paracca-team.png"
                  alt="Don Paracca Financial Advisor Nationwide Service - Paracca Financial Group Team"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="font-bellefair text-2xl  text-[#0a69b2] mb-2 uppercase">
                  Don Paracca
                </h3>
                <p className="font-montserrat text-sm font-semibold text-gray-500 mb-3 uppercase tracking-wide">
                  MBA
                </p>
                <p className="font-montserrat text-base text-[#0a69b2] font-semibold mb-3">
                  President & CEO
                </p>
                <p className="font-montserrat text-gray-600 leading-relaxed">
                  Founder and visionary behind Paracca Financial Group's holistic approach to wealth management.
                </p>
              </div>
            </div>

            {/* John Daniels */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition-shadow duration-300">
              <div className="relative h-80 overflow-hidden">
                <img 
                  src="/john-daniels-new.png"
                  alt="John Daniels CPA Tax Services Nationwide Service - Paracca Financial"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="font-bellefair text-2xl  text-[#0a69b2] mb-2 uppercase">
                  John Daniels
                </h3>
                <p className="font-montserrat text-sm font-semibold text-gray-500 mb-3 uppercase tracking-wide">
                  CPA
                </p>
                <p className="font-montserrat text-base text-[#0a69b2] font-semibold mb-3">
                  Tax Planning & Strategy
                </p>
                <p className="font-montserrat text-gray-600 leading-relaxed">
                  Expert in tax optimization and strategic planning for individuals.
                </p>
              </div>
            </div>

            {/* Kevin Hackett */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition-shadow duration-300">
              <div className="relative h-80 overflow-hidden">
                <img 
                  src="/kevin-hackett.png"
                  alt="Kevin Hackett CPA Estate Planning Nationwide Service Nationwide"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="font-bellefair text-2xl  text-[#0a69b2] mb-2 uppercase">
                  Kevin Hackett
                </h3>
                <p className="font-montserrat text-sm font-semibold text-gray-500 mb-3 uppercase tracking-wide">
                  CPA
                </p>
                <p className="font-montserrat text-base text-[#0a69b2] font-semibold mb-3">
                  Tax Planning & Strategy
                </p>
                <p className="font-montserrat text-gray-600 leading-relaxed">
                  Specializes in comprehensive tax services and wealth preservation strategies.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-bellefair text-3xl md:text-4xl  text-[#0a69b2] mb-4 uppercase">
              Our Values
            </h2>
            <p className="font-montserrat text-lg text-gray-600">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value) => {
              const IconComponent = value.icon;
              return (
                <Card key={value.title} className="text-center hover:shadow-xl transition-shadow duration-300">
                  <CardContent className="p-6">
                    <div className="bg-[#0a69b2] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="text-white" size={32} />
                    </div>
                    <h3 className="font-bellefair text-xl  text-[#0a69b2] mb-3 uppercase">
                      {value.title}
                    </h3>
                    <p className="font-montserrat text-gray-600 leading-relaxed">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-bellefair text-3xl md:text-4xl  text-[#0a69b2] mb-4 uppercase">
              Our Team
            </h2>
            <p className="font-montserrat text-lg text-gray-600 max-w-3xl mx-auto">
              A collaborative team of licensed professionals working together for your success
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {teamMembers.map((member) => (
              <Card key={member.name} className="overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <div className="relative h-64">
                  <img 
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a69b2] to-transparent opacity-60"></div>
                </div>
                <CardContent className="p-6">
                  <h3 className="font-bellefair text-2xl  text-[#0a69b2] mb-2 uppercase">
                    {member.name}
                  </h3>
                  <p className="font-montserrat text-sm font-semibold text-gray-500 mb-3 uppercase tracking-wide">
                    {member.role}
                  </p>
                  <p className="font-montserrat text-gray-600 leading-relaxed">
                    {member.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What Sets Us Apart */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-bellefair text-3xl md:text-4xl  text-[#0a69b2] mb-4 uppercase">
              What Sets Us Apart
            </h2>
            <p className="font-montserrat text-lg text-gray-600">
              The Paracca difference in wealth management
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {differentiators.map((item) => {
              const IconComponent = item.icon;
              return (
                <div 
                  key={item.title}
                  className="flex gap-4 p-6 bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="flex-shrink-0">
                    <div className="bg-[#0a69b2] w-12 h-12 rounded-lg flex items-center justify-center">
                      <IconComponent className="text-white" size={24} />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bellefair text-xl  text-[#0a69b2] mb-2 uppercase">
                      {item.title}
                    </h3>
                    <p className="font-montserrat text-gray-600 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-bellefair text-3xl md:text-4xl  text-[#0a69b2] mb-6 uppercase">
            Serving the Nationwide area & Beyond
          </h2>
          <p className="font-montserrat text-lg text-gray-600 leading-relaxed mb-8">
            Based in the Nationwide area, Florida, we proudly serve individuals and families throughout the Nationwide area, and Southwest Florida. Our estate planning services extend nationwide with attorneys licensed in all 50 states.
          </p>
          <div className="bg-gray-50 p-8 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="font-bellefair text-3xl  text-[#0a69b2] mb-2">the Nationwide area</div>
                <p className="font-montserrat text-sm text-gray-600">Primary Service Area</p>
              </div>
              <div>
                <div className="font-bellefair text-3xl  text-[#0a69b2] mb-2">Sarasota County</div>
                <p className="font-montserrat text-sm text-gray-600">Regional Coverage</p>
              </div>
              <div>
                <div className="font-bellefair text-3xl  text-[#0a69b2] mb-2">All 50 States</div>
                <p className="font-montserrat text-sm text-gray-600">Complete Services Nationwide</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0a69b2] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-bellefair text-3xl md:text-4xl  mb-6 uppercase">
            Experience the Paracca Difference
          </h2>
          <p className="font-montserrat text-lg mb-8 leading-relaxed text-gray-100">
            Discover how our holistic approach can transform your financial future
          </p>
          <a href="tel:941-545-9057">
            <button className="bg-white text-[#0a69b2] hover:bg-gray-100 font-montserrat font-semibold text-lg px-10 py-4 rounded transition-all duration-300 hover:shadow-2xl">
              Schedule a Consultation
            </button>
          </a>
        </div>
      </section>

      {/* FAQ Section - Optimized for AI Search */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-bellefair text-4xl md:text-5xl  text-[#0a69b2] mb-4 uppercase">
              Frequently Asked Questions
            </h2>
            <p className="font-montserrat text-lg text-gray-600">
              Common questions about our financial services in Nationwide, FL
            </p>
          </div>
          
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-bellefair text-xl text-[#0a69b2] mb-3 uppercase">
                What services does Paracca Financial Group offer in Nationwide?
              </h3>
              <p className="font-montserrat text-gray-700 leading-relaxed">
                Paracca Financial Group is a comprehensive financial advisory firm in Nationwide, Florida offering three core services: Asset Management with proprietary options overlay strategies, Tax services with in-house CPAs nationwide, and Estate Planning with licensed attorneys available in all 50 states. We serve individuals and families throughout the Nationwide area and Southwest Florida.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-bellefair text-xl text-[#0a69b2] mb-3 uppercase">
                Is Paracca Financial Group a fiduciary?
              </h3>
              <p className="font-montserrat text-gray-700 leading-relaxed">
                Yes, Paracca Financial Group is a FINRA registered fiduciary investment advisory firm. As a fiduciary, we are legally obligated to act in the best interests of our clients at all times, putting your financial goals ahead of our own interests.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-bellefair text-xl text-[#0a69b2] mb-3 uppercase">
                Where is Paracca Financial Group located?
              </h3>
              <p className="font-montserrat text-gray-700 leading-relaxed">
                Our office is located at 428 S Tamiami Trail, Osprey, FL 34229, serving the greater Nationwide area. We're conveniently accessible to clients in Nationwide County and throughout Southwest Florida. You can reach us at 941-545-9057 or contact@paracca.com.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-bellefair text-xl text-[#0a69b2] mb-3 uppercase">
                What makes Paracca Financial Group different from other financial advisors?
              </h3>
              <p className="font-montserrat text-gray-700 leading-relaxed">
                We are the only firm offering comprehensive asset management, tax, and estate services under one roof. Our team includes licensed financial advisors, in-house CPAs, and estate attorneys, providing truly holistic financial planning. With over 50 years combined experience and licensing in all 50 states, we deliver integrated solutions that traditional advisors cannot match.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-bellefair text-xl text-[#0a69b2] mb-3 uppercase">
                Do you provide legal advice?
              </h3>
              <p className="font-montserrat text-gray-700 leading-relaxed">
                Paracca Financial Group is not a law firm and does not provide legal advice. However, our in-house estate planning companies have licensed attorneys who provide legal services for estate planning, trusts, and wills. These legal services are separate from our investment advisory services.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-bellefair text-xl text-[#0a69b2] mb-3 uppercase">
                What are your office hours?
              </h3>
              <p className="font-montserrat text-gray-700 leading-relaxed">
                Our office hours are Monday through Friday, 8:30 AM to 4:00 PM EST. We're committed to providing personalized service to our clients in the Nationwide area and beyond.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-bellefair text-3xl md:text-4xl  text-[#0a69b2] mb-4 uppercase">
              Get In Touch
            </h2>
            <p className="font-montserrat text-lg text-gray-600">
              Ready to start your journey with us?
            </p>
          </div>
          <ContactForm />
        </div>
      </section>
    </>
  );
};

export default About;
